import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Plus, Trash2, Save, DollarSign, Package, MapPin, Clock } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import api from '../utils/api';

const CreateQuote = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [company, setCompany] = useState(null);
  
  const [form, setForm] = useState({
    title: '',
    origin: '',
    destination: '',
    cargo_type: 'general',
    transport_mode: 'sea',
    weight: '',
    volume: '',
    quantity: '',
    quantity_unit: 'units',
    unit_price: '',
    currency: 'USD',
    base_rate: '',
    additional_charges: [],
    transit_time: '',
    validity_days: '7',
    notes: '',
    terms_conditions: ''
  });

  useEffect(() => {
    fetchCompany();
  }, []);

  const fetchCompany = async () => {
    try {
      const response = await api.get('/companies/me');
      setCompany(response.data);
    } catch (error) {
      console.error('Failed to fetch company:', error);
    }
  };

  const addCharge = () => {
    setForm(prev => ({
      ...prev,
      additional_charges: [...prev.additional_charges, { description: '', amount: '' }]
    }));
  };

  const removeCharge = (index) => {
    setForm(prev => ({
      ...prev,
      additional_charges: prev.additional_charges.filter((_, i) => i !== index)
    }));
  };

  const updateCharge = (index, field, value) => {
    setForm(prev => ({
      ...prev,
      additional_charges: prev.additional_charges.map((charge, i) => 
        i === index ? { ...charge, [field]: value } : charge
      )
    }));
  };

  const calculateTotal = () => {
    const base = parseFloat(form.base_rate) || 0;
    const quantity = parseFloat(form.quantity) || 1;
    const additional = form.additional_charges.reduce((sum, charge) => {
      return sum + (parseFloat(charge.amount) || 0);
    }, 0);
    // If unit price is set, use quantity * unit_price, otherwise use base_rate
    if (form.unit_price && form.quantity) {
      return (parseFloat(form.unit_price) * quantity) + additional;
    }
    return base + additional;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.title || !form.origin || !form.destination || !form.base_rate) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...form,
        base_rate: parseFloat(form.base_rate),
        weight: form.weight ? parseFloat(form.weight) : null,
        volume: form.volume ? parseFloat(form.volume) : null,
        quantity: form.quantity ? parseFloat(form.quantity) : null,
        unit_price: form.unit_price ? parseFloat(form.unit_price) : null,
        validity_days: parseInt(form.validity_days),
        total_price: calculateTotal(),
        additional_charges: form.additional_charges.map(c => ({
          description: c.description,
          amount: parseFloat(c.amount) || 0
        })).filter(c => c.description && c.amount > 0)
      };

      await api.post('/quotes/marketplace', payload);
      toast.success('Quote published to marketplace!');
      navigate('/quotes');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create quote');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-slate-50 p-4 md:p-8">
        <div className="max-w-3xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-6"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-6 w-6 text-green-500" />
                Create Marketplace Quote
              </CardTitle>
              <CardDescription>
                Publish a quote that will be visible to all members in the marketplace
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Info */}
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="title">Quote Title *</Label>
                    <Input
                      id="title"
                      value={form.title}
                      onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="e.g., Miami to Kingston FCL Shipping"
                      required
                      data-testid="quote-title"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="origin">Origin *</Label>
                      <Input
                        id="origin"
                        value={form.origin}
                        onChange={(e) => setForm(prev => ({ ...prev, origin: e.target.value }))}
                        placeholder="e.g., Miami, FL"
                        required
                        data-testid="quote-origin"
                      />
                    </div>
                    <div>
                      <Label htmlFor="destination">Destination *</Label>
                      <Input
                        id="destination"
                        value={form.destination}
                        onChange={(e) => setForm(prev => ({ ...prev, destination: e.target.value }))}
                        placeholder="e.g., Kingston, Jamaica"
                        required
                        data-testid="quote-destination"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Transport Mode</Label>
                      <Select 
                        value={form.transport_mode} 
                        onValueChange={(v) => setForm(prev => ({ ...prev, transport_mode: v }))}
                      >
                        <SelectTrigger data-testid="quote-mode">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="sea">Sea Freight</SelectItem>
                          <SelectItem value="air">Air Freight</SelectItem>
                          <SelectItem value="land">Land/Truck</SelectItem>
                          <SelectItem value="multimodal">Multimodal</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Cargo Type</Label>
                      <Select 
                        value={form.cargo_type} 
                        onValueChange={(v) => setForm(prev => ({ ...prev, cargo_type: v }))}
                      >
                        <SelectTrigger data-testid="quote-cargo">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {/* Pallet Options */}
                          <SelectItem value="pallet_6ft">Pallet (Up to 6ft)</SelectItem>
                          <SelectItem value="half_pallet">Half Pallet</SelectItem>
                          <SelectItem value="full_pallet">Full Pallet (Standard 48x40)</SelectItem>
                          <SelectItem value="euro_pallet">Euro Pallet (48x32)</SelectItem>
                          <SelectItem value="oversized_pallet">Oversized Pallet</SelectItem>
                          {/* Container Options */}
                          <SelectItem value="fcl_20">FCL 20ft Container</SelectItem>
                          <SelectItem value="fcl_40">FCL 40ft Container</SelectItem>
                          <SelectItem value="fcl_40hc">FCL 40ft High Cube</SelectItem>
                          <SelectItem value="lcl">LCL (Less Container Load)</SelectItem>
                          {/* Package Types */}
                          <SelectItem value="parcel">Parcel/Small Package</SelectItem>
                          <SelectItem value="box">Box/Carton</SelectItem>
                          <SelectItem value="crate">Crate</SelectItem>
                          <SelectItem value="drum">Drum/Barrel</SelectItem>
                          <SelectItem value="bags">Bags/Sacks</SelectItem>
                          <SelectItem value="bundle">Bundle</SelectItem>
                          {/* Weight-Based */}
                          <SelectItem value="per_kg">Per Kilogram</SelectItem>
                          <SelectItem value="per_lb">Per Pound</SelectItem>
                          <SelectItem value="per_cbm">Per Cubic Meter</SelectItem>
                          <SelectItem value="per_cft">Per Cubic Foot</SelectItem>
                          {/* Special Cargo */}
                          <SelectItem value="air_cargo">Air Cargo</SelectItem>
                          <SelectItem value="bulk">Bulk Cargo</SelectItem>
                          <SelectItem value="breakbulk">Breakbulk</SelectItem>
                          <SelectItem value="roro">RoRo (Roll-on/Roll-off)</SelectItem>
                          <SelectItem value="vehicle">Vehicle</SelectItem>
                          <SelectItem value="heavy_equipment">Heavy Equipment</SelectItem>
                          {/* Temperature Controlled */}
                          <SelectItem value="reefer">Reefer (Refrigerated)</SelectItem>
                          <SelectItem value="frozen">Frozen Goods</SelectItem>
                          <SelectItem value="chilled">Chilled Goods</SelectItem>
                          {/* Specialty */}
                          <SelectItem value="hazmat">Hazmat/Dangerous Goods</SelectItem>
                          <SelectItem value="perishable">Perishable</SelectItem>
                          <SelectItem value="fragile">Fragile</SelectItem>
                          <SelectItem value="livestock">Livestock</SelectItem>
                          <SelectItem value="general">General Cargo</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="weight">Weight (kg)</Label>
                      <Input
                        id="weight"
                        type="number"
                        value={form.weight}
                        onChange={(e) => setForm(prev => ({ ...prev, weight: e.target.value }))}
                        placeholder="Optional"
                      />
                    </div>
                    <div>
                      <Label htmlFor="volume">Volume (CBM)</Label>
                      <Input
                        id="volume"
                        type="number"
                        value={form.volume}
                        onChange={(e) => setForm(prev => ({ ...prev, volume: e.target.value }))}
                        placeholder="Optional"
                      />
                    </div>
                  </div>
                  
                  {/* Quantity for Unit Pricing */}
                  <div className="grid grid-cols-3 gap-4 mt-4">
                    <div>
                      <Label htmlFor="quantity">Quantity</Label>
                      <Input
                        id="quantity"
                        type="number"
                        step="0.01"
                        value={form.quantity}
                        onChange={(e) => setForm(prev => ({ ...prev, quantity: e.target.value }))}
                        placeholder="1"
                      />
                    </div>
                    <div>
                      <Label>Quantity Unit</Label>
                      <Select 
                        value={form.quantity_unit} 
                        onValueChange={(v) => setForm(prev => ({ ...prev, quantity_unit: v }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="units">Units</SelectItem>
                          <SelectItem value="pallets">Pallets</SelectItem>
                          <SelectItem value="containers">Containers</SelectItem>
                          <SelectItem value="cbm">CBM</SelectItem>
                          <SelectItem value="kg">KG</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="unit_price">Unit Price</Label>
                      <Input
                        id="unit_price"
                        type="number"
                        step="0.01"
                        value={form.unit_price}
                        onChange={(e) => setForm(prev => ({ ...prev, unit_price: e.target.value }))}
                        placeholder="Price per unit"
                      />
                      <p className="text-xs text-slate-500 mt-1">Optional: Used for total calculation</p>
                    </div>
                  </div>
                </div>

                {/* Pricing */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-green-500" />
                    Pricing
                  </h3>
                  
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <Label>Currency</Label>
                      <Select 
                        value={form.currency} 
                        onValueChange={(v) => setForm(prev => ({ ...prev, currency: v }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="USD">USD ($)</SelectItem>
                          <SelectItem value="EUR">EUR (€)</SelectItem>
                          <SelectItem value="GBP">GBP (£)</SelectItem>
                          <SelectItem value="JMD">JMD (J$)</SelectItem>
                          <SelectItem value="TTD">TTD (TT$)</SelectItem>
                          <SelectItem value="BBD">BBD (Bds$)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="base_rate">Base Rate *</Label>
                      <Input
                        id="base_rate"
                        type="number"
                        step="0.01"
                        value={form.base_rate}
                        onChange={(e) => setForm(prev => ({ ...prev, base_rate: e.target.value }))}
                        placeholder="0.00"
                        required
                        data-testid="quote-rate"
                      />
                    </div>
                  </div>

                  {/* Additional Charges */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label>Additional Charges</Label>
                      <Button type="button" variant="outline" size="sm" onClick={addCharge}>
                        <Plus className="h-4 w-4 mr-1" />
                        Add Charge
                      </Button>
                    </div>
                    {form.additional_charges.map((charge, index) => (
                      <div key={index} className="flex gap-3 items-center">
                        <Input
                          value={charge.description}
                          onChange={(e) => updateCharge(index, 'description', e.target.value)}
                          placeholder="Description (e.g., Fuel Surcharge)"
                          className="flex-1"
                        />
                        <Input
                          type="number"
                          step="0.01"
                          value={charge.amount}
                          onChange={(e) => updateCharge(index, 'amount', e.target.value)}
                          placeholder="Amount"
                          className="w-32"
                        />
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeCharge(index)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>

                  {/* Total */}
                  <div className="mt-4 p-4 bg-green-50 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold">Total Price:</span>
                      <span className="text-2xl font-bold text-green-600">
                        {form.currency} {calculateTotal().toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Terms */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Clock className="h-5 w-5 text-cyan-500" />
                    Terms & Validity
                  </h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="transit_time">Transit Time</Label>
                      <Input
                        id="transit_time"
                        value={form.transit_time}
                        onChange={(e) => setForm(prev => ({ ...prev, transit_time: e.target.value }))}
                        placeholder="e.g., 5-7 days"
                      />
                    </div>
                    <div>
                      <Label>Quote Valid For</Label>
                      <Select 
                        value={form.validity_days} 
                        onValueChange={(v) => setForm(prev => ({ ...prev, validity_days: v }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="3">3 Days</SelectItem>
                          <SelectItem value="7">7 Days</SelectItem>
                          <SelectItem value="14">14 Days</SelectItem>
                          <SelectItem value="30">30 Days</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="mt-4">
                    <Label htmlFor="notes">Additional Notes</Label>
                    <Textarea
                      id="notes"
                      value={form.notes}
                      onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Any additional information about this quote..."
                      rows={3}
                    />
                  </div>

                  <div className="mt-4">
                    <Label htmlFor="terms">Terms & Conditions</Label>
                    <Textarea
                      id="terms"
                      value={form.terms_conditions}
                      onChange={(e) => setForm(prev => ({ ...prev, terms_conditions: e.target.value }))}
                      placeholder="Payment terms, insurance requirements, etc."
                      rows={3}
                    />
                  </div>
                </div>

                {/* Submit */}
                <div className="flex justify-end gap-4 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => navigate(-1)}>
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    className="bg-green-500 hover:bg-green-600"
                    disabled={loading}
                    data-testid="submit-quote"
                  >
                    <Save className="mr-2 h-4 w-4" />
                    {loading ? 'Publishing...' : 'Publish to Marketplace'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default CreateQuote;
